import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { ColumnDef } from '@tanstack/react-table';

import { DataRow, useTableViewState } from '~components/table-view';
import EditableTableModal from '~support-at-home/components/common/EditableTableModal';

import { ClientContribution } from '~support-at-home/models';

import columnsFactory from '~support-at-home/forms/client-contributions/listSingle';

import { create, deleteOne, getAll, update } from '~support-at-home/actions/clientContributions';
import { getAll as getContributionCategories } from '~lookups/actions/sahCategories';

import { selectSahCategories } from '~lookups/selectors/sahCategories';
import { selectSahClientContributions } from '~support-at-home/selectors/clientContributions';
import { selectSahFundingTypes } from '~support-at-home/selectors/fundingTypes';

import { filterRowFields } from '../helpers';

interface ComponentProps {
  id: number | null;
  isOpen?: boolean;
  onClose: (confirm: boolean) => void;
}

const ClientContributionsModal: React.FC<ComponentProps> = ({ id, isOpen = false, onClose }) => {
  const dispatch = useAppDispatch();
  const clientContributions = useAppSelector(selectSahClientContributions);
  const fundingSources = useAppSelector(selectSahFundingTypes);
  const categories = useAppSelector(selectSahCategories);
  const { data, setData } = useTableViewState<DataRow<ClientContribution>>(
    clientContributions as DataRow<ClientContribution>[],
  );
  const combinedData = data;
  const columns: ColumnDef<DataRow<ClientContribution>>[] = columnsFactory(categories, fundingSources) as ColumnDef<
    DataRow<ClientContribution>
  >[];
  const getDefaultRow = () => {
    return {
      id: crypto.randomUUID(),
      start_date: '',
      end_date: '',
    } as DataRow<ClientContribution>;
  };

  const load = async () => {
    dispatch(getAll({ client_id: id }));
  };

  useEffect(() => {
    if (isOpen && id !== null) {
      dispatch(getContributionCategories({ sort: ['sequence'] }));
      load();
    }
  }, [id, isOpen]);

  useEffect(() => {
    const persistedContributions = clientContributions.map((contribution) => ({
      ...contribution,
      persisted: true,
      backendId: contribution.id,
    })) as DataRow<ClientContribution>[];

    setData(persistedContributions);
  }, [clientContributions]);

  const onDependentChange = (
    row: DataRow<ClientContribution>,
    columnId: string,
    _value: unknown,
  ): DataRow<ClientContribution> => {
    if (columnId === 'category_id') {
      const catId = typeof row.category_id === 'string' ? row.category_id : '';
      const cat = categories.find((c) => c.id === catId);
      const max = cat?.max_contrib_proportion ?? 0;
      return {
        ...row,
        category: {
          ...(typeof row.category === 'object' && row.category !== null ? row.category : {}),
          max_contrib_proportion: max,
        },
      };
    }
    return row;
  };

  return (
    <EditableTableModal
      isOpen={isOpen}
      title="Allocated SAH Contributions"
      data={combinedData}
      columns={columns}
      onClose={(shouldReload) => {
        onClose(shouldReload);
        if (shouldReload) load();
      }}
      onCreate={async (row) => {
        const toCreate = filterRowFields(row, columns, id);
        await dispatch(create(toCreate)).unwrap();
        await load();
      }}
      onUpdate={async (row) => {
        if (!row.backendId) {
          return;
        }
        const toUpdate = filterRowFields(row, columns, id);
        await dispatch(update({ ...toUpdate, id: row.backendId })).unwrap();
        await load();
      }}
      onDelete={async (row) => {
        if (!row.backendId) {
          return;
        }
        await dispatch(deleteOne({ id: row.backendId })).unwrap();
        await load();
      }}
      getDefaultRow={getDefaultRow}
      allowEdit={true}
      allowAdd={true}
      allowDelete={true}
      onDependentChange={onDependentChange}
    />
  );
};
export default ClientContributionsModal;
